import { useState, useEffect, useRef } from 'react';
import { setlistsApi } from '@/api/setlists';
import type { Setlist } from '@/types/setlist';

interface Props {
  teamId: string;
  currentSetlistName?: string;
  onLoad: (setlistId: string) => void;
  theme: any;
}

export default function LoadSetlistPicker({ teamId, currentSetlistName, onLoad, theme }: Props) {
  const [open, setOpen] = useState(false);
  const [setlists, setSetlists] = useState<Setlist[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    let isMounted = true;
    setLoading(true);
    setLoadError(false);
    setlistsApi.list(teamId)
      .then((res) => {
        if (isMounted) setSetlists(res.data);
      })
      .catch(() => {
        if (isMounted) {
          setSetlists([]);
          setLoadError(true);
        }
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [open, teamId]);

  useEffect(() => {
    if (!open) return;
    const handleOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open]);

  return (
    <div ref={containerRef} className="relative px-3 pb-3">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors w-full"
        style={{
          backgroundColor: open ? `${theme.colors.accent}20` : theme.colors.bg.primary,
          color: currentSetlistName ? theme.colors.accent : theme.colors.text.secondary,
          border: `1px solid ${open ? theme.colors.accent + '40' : theme.colors.border}`,
        }}
      >
        <i className="ri-list-check-2 text-sm shrink-0"></i>
        <span className="truncate flex-1 text-left">{currentSetlistName ?? 'Carregar setlist'}</span>
        <i className={`ri-arrow-${open ? 'up' : 'down'}-s-line text-sm shrink-0`}></i>
      </button>

      {open && (
        <div
          className="absolute left-3 right-3 mt-1 rounded-xl border shadow-xl z-50 overflow-hidden max-h-56 overflow-y-auto"
          style={{ backgroundColor: theme.colors.bg.primary, borderColor: theme.colors.accent + '60', opacity: 0.98 }}
        >
          {loading ? (
            <div className="flex justify-center py-5">
              <div className="w-4 h-4 border-2 rounded-full animate-spin" style={{
                borderColor: `${theme.colors.accent}30`,
                borderTopColor: theme.colors.accent,
              }}></div>
            </div>
          ) : loadError ? (
            <p role="alert" className="text-xs text-center py-4" style={{ color: theme.colors.text.tertiary }}>
              Erro ao carregar setlists.
            </p>
          ) : setlists.length === 0 ? (
            <p className="text-xs text-center py-4" style={{ color: theme.colors.text.tertiary }}>Nenhum setlist.</p>
          ) : (
            setlists.map((sl) => (
              <button
                key={sl.id}
                onClick={() => { onLoad(sl.id); setOpen(false); }}
                className="w-full flex items-center gap-2 px-4 py-3 text-left transition-colors"
                style={{
                  backgroundColor: currentSetlistName === sl.name ? `${theme.colors.accent}15` : 'transparent',
                  color: theme.colors.text.primary,
                  borderBottom: `1px solid ${theme.colors.border}`,
                }}
              >
                <i className="ri-list-check-2 text-xs shrink-0" style={{ color: theme.colors.text.tertiary }}></i>
                <span className="text-sm truncate">{sl.name}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
